import {useEffect, useState, useMemo} from 'react';
import {
  ChevronRightIcon,
  ArrowPathIcon,
  ArrowUturnRightIcon,
} from '@heroicons/react/24/outline';
import {useIntl} from 'react-intl';
import {useNavigate} from 'react-router-dom';
import debounce from 'lodash/debounce';

import {useJobLocation} from '../../hooks/useJobLocation';
import {currencyFormat} from '../../../../utils/currency';

import SectionWrapper from '../../../../components/SectionWrapper';
import {FormatDate} from '../../../../components/FormatDate';
import Button from '../../../../components/Button';
import IconButton from '../../../../components/IconButton';
import Search from '../../../../components/Search';
import Table from '../../../../components/Table';
import Badge from '../../../../components/Badge';
import Select from '../../../../components/_form/Select';

const JobLocationPage = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const {getJobs, jobs, syncPunches} = useJobLocation();
  const {formatMessage} = useIntl();

  const [activeTab, setActiveTab] = useState('active');
  const [search, setSearch] = useState('');
  const [searchFiled, setSearchField] = useState('name');

  const tabItems = [
    {
      value: 'all',
      label: formatMessage({id: 'all'}),
    },
    {
      value: 'active',
      label: formatMessage({id: 'active'}),
    },
    {
      value: 'done',
      label: formatMessage({id: 'done'}),
    },
    {
      value: 'inactive',
      label: formatMessage({id: 'inactive'}),
    },
  ];

  const handleFetchJobs = (searchTerm = '') => {
    getJobs({
      type: 'all',
      status: activeTab,
      per_page: 500,
      page: 1,
      [searchFiled]: searchTerm || search,
    });
  };

  const handleSearch = (term = '') => {
    setSearch(term);
    handleFetchJobs(term);
  };

  useEffect(() => {
    handleFetchJobs();
  }, [activeTab, searchFiled]);

  const searchDebounce = debounce(handleSearch, 500);

  const columns = useMemo(
    () => [
      {
        Header: `${formatMessage({id: 'name'})}`,
        accessor: 'name',
        Cell: ({row, value}) => {
          const hasAddress =
            row.original.location &&
            row.original.location.coordinates &&
            row.original.location.coordinates[0] &&
            row.original.location.coordinates[1] &&
            row.original.location.address;

          return (
            <>
              {row.original.parent_id && (
                <Badge variant="danger">
                  <ArrowUturnRightIcon
                    className="mr-2 h-3 w-3"
                    aria-hidden="true"
                  />
                  {row.original.parent_id.name}
                </Badge>
              )}
              {row.original.childrens && row.original.childrens.length > 0 && (
                <Badge variant="info">
                  {row.original.childrens.length} subs
                </Badge>
              )}
              <p>{value}</p>
              {hasAddress ? (
                <p className="text-xs text-gray-500">
                  {row.original.location.address}
                </p>
              ) : (
                <Badge variant="error">
                  {formatMessage({id: 'joblocation.no-adress'})}
                </Badge>
              )}
            </>
          );
        },
      },
      {
        accessor: 'budget.$numberDecimal',
        Header: `${formatMessage({id: 'budget'})}`,
        Cell: ({value}) => currencyFormat(value),
      },
      {
        accessor: 'status',
        Header: `${formatMessage({id: 'status'})}`,
      },
      {
        accessor: 'started_at',
        Header: `${formatMessage({id: 'started-at'})}`,
        Cell: ({value}) => <FormatDate value={value} />,
      },
      {
        accessor: 'updated_at',
        Header: `${formatMessage({id: 'updated-at'})}`,
        Cell: ({value}) => <FormatDate value={value} />,
      },
      {
        accessor: 'created_at',
        Header: `${formatMessage({id: 'created-at'})}`,
        Cell: ({value}) => <FormatDate value={value} />,
      },
      {
        Header: '',
        accessor: '_id',
        Cell: ({row}) => (
          <div className="flex">
            <Button
              variant="secondary"
              extraClassName="shadow-sm mr-2"
              onClick={() => syncPunches(row.original._id)}>
              <>
                <ArrowPathIcon
                  className="-ml-1 mr-2 h-5 w-5"
                  aria-hidden="true"
                />
                {intl.formatMessage({id: 'joblocation-sync-punches'})}
              </>
            </Button>
            <IconButton
              icon={ChevronRightIcon}
              onClick={() => navigate(`/job-location/${row.original._id}`)}
            />
          </div>
        ),
      },
    ],
    [formatMessage],
  );

  return (
    <SectionWrapper
      isLoading={jobs.loading}
      tabItems={tabItems}
      onSelectTab={(val) => setActiveTab(val)}
      activeTab={activeTab}
      toolbar={
        <>
          <div style={{minWidth: 120}}>
            <Select
              label=""
              id="search-field"
              className=""
              options={[
                {
                  label: formatMessage({id: 'name'}),
                  value: 'name',
                },
                {
                  label: formatMessage({id: 'address'}),
                  value: 'address',
                },
                {
                  label: formatMessage({id: 'city'}),
                  value: 'city',
                },
              ]}
              defaultValue="name"
              value={searchFiled}
              onChange={(e) => setSearchField(e)}
              placeholder="Select the field to search"
            />
          </div>
          <Search onChange={(e) => searchDebounce(e.target.value)} />
        </>
      }>
      <Table columns={columns} data={jobs.data} />
    </SectionWrapper>
  );
};

export default JobLocationPage;
